import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  User, Bell, Save, CheckCircle,
  Eye, EyeOff, AlertCircle, Lock, Key,
} from 'lucide-react';

/* ── toggle switch ────────────────────────────────────────────────────── */
function Toggle({ on, onToggle }) {
  return (
    <button
      onClick={onToggle}
      role="switch"
      aria-checked={on}
      className={`relative w-9 h-5 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#00E5FF]/40 ${on ? 'bg-[#00E5FF]' : 'bg-white/15'}`}
    >
      <span
        className="absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-all duration-200"
        style={{ left: on ? '18px' : '2px' }}
      />
    </button>
  );
}

/* ── card section ─────────────────────────────────────────────────────── */
function Section({ icon: Icon, title, children, accent = '#00E5FF' }) {
  return (
    <div className="glass-card p-6 mb-4 border border-white/[0.06]">
      <h2 className="text-sm font-semibold text-slate-300 mb-5 flex items-center gap-2 pb-4 border-b border-white/[0.05]">
        <Icon size={14} style={{ color: accent }} />
        {title}
      </h2>
      {children}
    </div>
  );
}

/* ── password field ───────────────────────────────────────────────────── */
function PwdField({ label, id, value, onChange, show, onToggleShow, placeholder, error, disabled }) {
  return (
    <div>
      <label htmlFor={id} className="block text-xs text-slate-500 mb-1.5">{label}</label>
      <div className="relative">
        <input
          id={id}
          type={show ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="new-password"
          className={[
            'w-full bg-white/[0.04] border rounded-xl px-3 pr-10 py-2.5 text-sm text-slate-300',
            'placeholder-slate-700 outline-none transition-colors',
            error ? 'border-red-500/40 focus:border-red-500/60' : 'border-white/[0.08] focus:border-[#00E5FF]/40',
            disabled ? 'opacity-50 cursor-not-allowed' : '',
          ].join(' ')}
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={onToggleShow}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-600 hover:text-slate-400 transition-colors"
        >
          {show ? <EyeOff size={14} /> : <Eye size={14} />}
        </button>
      </div>
      {error && (
        <p className="mt-1 text-xs text-red-400 flex items-center gap-1">
          <AlertCircle size={10} /> {error}
        </p>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════ */
export default function SettingsPage() {
  const { user, token, API_URL } = useAuth();

  /* ── notifications state ─────────────────────────────────────────── */
  const [notifs, setNotifs] = useState({
    highRisk: true,
    reports:  false,
    apiLimit: true,
    news:     false,
  });
  const [notifSaved, setNotifSaved] = useState(false);
  const toggle = key => setNotifs(n => ({ ...n, [key]: !n[key] }));
  const saveNotifs = () => { setNotifSaved(true); setTimeout(() => setNotifSaved(false), 2500); };

  /* ── change password state ───────────────────────────────────────── */
  const [pwd, setPwd] = useState({ current: '', next: '', confirm: '' });
  const [pwdShow, setPwdShow]   = useState({ current: false, next: false, confirm: false });
  const [pwdErr, setPwdErr]     = useState({});
  const [pwdStatus, setPwdStatus] = useState(''); // '' | 'saving' | 'saved' | 'error'
  const [pwdMsg, setPwdMsg]     = useState('');

  const isGoogleOnly = user && !user.hasPassword;

  const validatePwd = () => {
    const errs = {};
    if (!isGoogleOnly && !pwd.current)  errs.current = 'Current password is required';
    if (!pwd.next)                       errs.next    = 'New password is required';
    else if (pwd.next.length < 8)        errs.next    = 'Must be at least 8 characters';
    if (!pwd.confirm)                    errs.confirm = 'Please confirm your password';
    else if (pwd.next !== pwd.confirm)   errs.confirm = 'Passwords do not match';
    setPwdErr(errs);
    return Object.keys(errs).length === 0;
  };

  const handleChangePwd = async (e) => {
    e.preventDefault();
    if (!validatePwd()) return;
    setPwdStatus('saving');
    setPwdMsg('');
    try {
      const r = await fetch(`${API_URL}/api/auth/change-password`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body:    JSON.stringify({ currentPassword: pwd.current, newPassword: pwd.next }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Failed to update password');
      setPwdStatus('saved');
      setPwdMsg('Password updated successfully!');
      setPwd({ current: '', next: '', confirm: '' });
      setTimeout(() => setPwdStatus(''), 3000);
    } catch (err) {
      setPwdStatus('error');
      setPwdMsg(err.message);
    }
  };

  return (
    <div className="p-6 max-w-2xl mx-auto">

      {/* Page header */}
      <div className="mb-7">
        <h1 className="text-xl font-bold text-white">Settings</h1>
        <p className="text-slate-500 text-sm mt-0.5">Manage your account preferences and security</p>
      </div>

      {/* ── Profile ──────────────────────────────────────────────────── */}
      <Section icon={User} title="Profile">
        <div className="flex items-center gap-4 mb-5">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#00E5FF] to-[#7C3AED] flex items-center justify-center text-lg font-bold text-white flex-shrink-0 shadow-lg">
            {(user?.name?.[0] || user?.email?.[0] || 'U').toUpperCase()}
          </div>
          <div>
            <div className="text-sm font-semibold text-white">{user?.name || 'Unnamed User'}</div>
            <div className="text-xs text-slate-500">{user?.email}</div>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-slate-600 mb-1.5">Email Address</label>
            <input
              type="email"
              defaultValue={user?.email}
              disabled
              className="w-full bg-white/[0.03] border border-white/[0.06] rounded-xl px-3 py-2.5 text-sm text-slate-500 cursor-not-allowed"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-600 mb-1.5">Current Plan</label>
            <div className="flex items-center gap-2 bg-white/[0.03] border border-white/[0.06] rounded-xl px-3 py-2.5">
              <span className="w-2 h-2 rounded-full bg-[#00E5FF] animate-pulse flex-shrink-0" />
              <span className="text-sm text-slate-300 capitalize font-medium">
                {user?.plan || 'Free'} Plan
              </span>
            </div>
          </div>
        </div>
      </Section>

      {/* ── Change Password ───────────────────────────────────────────── */}
      <Section icon={Lock} title="Change Password" accent="#7C3AED">
        {isGoogleOnly ? (
          <div className="flex items-start gap-3 p-4 bg-[#7C3AED]/8 border border-[#7C3AED]/15 rounded-xl">
            <Key size={14} className="text-[#7C3AED] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-slate-300 font-medium mb-0.5">Google-linked account</p>
              <p className="text-xs text-slate-500">
                Your account uses Google Sign-In. Password management is handled through your Google account settings.
              </p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleChangePwd} className="space-y-4">
            {pwdStatus === 'saved' && (
              <div className="flex items-center gap-2 bg-green-500/10 border border-green-500/20 text-green-400 rounded-xl px-4 py-3 text-sm">
                <CheckCircle size={14} /> {pwdMsg}
              </div>
            )}
            {pwdStatus === 'error' && (
              <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/20 text-red-400 rounded-xl px-4 py-3 text-sm">
                <AlertCircle size={14} className="flex-shrink-0 mt-0.5" /> {pwdMsg}
              </div>
            )}

            <PwdField
              label="Current Password"
              id="current-pwd"
              value={pwd.current}
              onChange={e => { setPwd(p => ({ ...p, current: e.target.value })); setPwdErr(r => ({ ...r, current: '' })); }}
              show={pwdShow.current}
              onToggleShow={() => setPwdShow(s => ({ ...s, current: !s.current }))}
              placeholder="Your current password"
              error={pwdErr.current}
              disabled={pwdStatus === 'saving'}
            />
            <PwdField
              label="New Password"
              id="new-pwd"
              value={pwd.next}
              onChange={e => { setPwd(p => ({ ...p, next: e.target.value })); setPwdErr(r => ({ ...r, next: '' })); }}
              show={pwdShow.next}
              onToggleShow={() => setPwdShow(s => ({ ...s, next: !s.next }))}
              placeholder="Min. 8 characters"
              error={pwdErr.next}
              disabled={pwdStatus === 'saving'}
            />
            <PwdField
              label="Confirm New Password"
              id="confirm-pwd"
              value={pwd.confirm}
              onChange={e => { setPwd(p => ({ ...p, confirm: e.target.value })); setPwdErr(r => ({ ...r, confirm: '' })); }}
              show={pwdShow.confirm}
              onToggleShow={() => setPwdShow(s => ({ ...s, confirm: !s.confirm }))}
              placeholder="Re-enter new password"
              error={pwdErr.confirm}
              disabled={pwdStatus === 'saving'}
            />

            <button
              type="submit"
              disabled={pwdStatus === 'saving'}
              className="flex items-center gap-2 text-sm font-bold px-5 py-2.5 rounded-xl bg-[#7C3AED] hover:bg-[#6d35d4] text-white transition-all disabled:opacity-50"
            >
              {pwdStatus === 'saving' ? (
                <><span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Saving…</>
              ) : (
                <><Lock size={13} /> Update Password</>
              )}
            </button>
          </form>
        )}
      </Section>

      {/* ── Notifications ─────────────────────────────────────────────── */}
      <Section icon={Bell} title="Notifications">
        <div className="space-y-5">
          {[
            { key: 'highRisk', label: 'High Risk Alerts',       desc: 'Notify when a scanned wallet is flagged as High risk' },
            { key: 'reports',  label: 'Report Ready',           desc: 'Notify when a new risk report is ready to view' },
            { key: 'apiLimit', label: 'API Rate Limit Warning',  desc: "Warn when approaching your plan's API rate limit" },
            { key: 'news',     label: 'Product Updates',        desc: 'Receive ZenithCipher product news and security advisories' },
          ].map(({ key, label, desc }) => (
            <div key={key} className="flex items-center justify-between gap-4">
              <div>
                <div className="text-sm text-slate-300 font-medium">{label}</div>
                <div className="text-xs text-slate-600 mt-0.5">{desc}</div>
              </div>
              <Toggle on={notifs[key]} onToggle={() => toggle(key)} />
            </div>
          ))}
        </div>
        <div className="mt-5 pt-4 border-t border-white/[0.05] flex items-center justify-between">
          <p className="text-xs text-slate-600">Notification preferences are saved locally</p>
          <button
            onClick={saveNotifs}
            className={[
              'flex items-center gap-2 text-xs font-bold px-4 py-2 rounded-xl transition-all',
              notifSaved
                ? 'bg-green-500/15 text-green-400 border border-green-500/25'
                : 'bg-[#00E5FF] hover:bg-[#00cce5] text-black',
            ].join(' ')}
          >
            {notifSaved ? <><CheckCircle size={12} /> Saved!</> : <><Save size={12} /> Save</>}
          </button>
        </div>
      </Section>

      {/* ── Danger zone ───────────────────────────────────────────────── */}
      <Section icon={AlertCircle} title="Danger Zone" accent="#f43f5e">
        <div className="flex items-center justify-between gap-4 p-4 border border-red-500/15 rounded-xl bg-red-500/5">
          <div>
            <div className="text-sm font-semibold text-white mb-0.5">Delete Account</div>
            <div className="text-xs text-slate-500 leading-relaxed">
              Permanently delete your account and all associated scan data. This cannot be undone.
            </div>
          </div>
          <button className="flex-shrink-0 text-xs font-bold px-4 py-2 rounded-xl border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-all">
            Delete
          </button>
        </div>
      </Section>

    </div>
  );
}
